import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import Envelope from 'components/Envelope';
import RootStore, { RootStoreContext } from 'stores/RootStore';
import PrimaryAuthenticator from 'services/PrimaryAuthenticator';
import PrimaryClient from 'services/PrimaryClient';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import * as yup from 'yup';
import yupLocale from 'utils/yupLocale';
import { LinearProgress } from '@mui/material';
import AppRouter from 'router/AppRouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

if (process.env.NODE_ENV === 'production') {
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN ?? null;
  if (sentryDsn !== null) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [new Integrations.BrowserTracing()],
      tracesSampleRate: 1.0,
    });
  }
}

yup.setLocale(yupLocale);

const store = new RootStore();
const baseURL = process.env.REACT_APP_API_HOST ?? '';
const authenticator = new PrimaryAuthenticator();
PrimaryClient.createClientInstance(baseURL, authenticator);

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

root.render(
  <QueryClientProvider client={queryClient}>
    <RootStoreContext.Provider value={store}>
      <Suspense fallback={<LinearProgress />}>
        <Envelope>
          <AppRouter />
        </Envelope>
      </Suspense>
    </RootStoreContext.Provider>
  </QueryClientProvider>
);

serviceWorkerRegistration.register();
